export type FootageLinkKind = 'youtube' | 'drive' | 'dropbox' | 's3' | 'video';

export interface FootageLink {
  url: string;
  kind: FootageLinkKind;
}

const URL_RE = /https?:\/\/[^\s<>"')\]]+/gi;
const VIDEO_EXT = /\.(mp4|mov|m4v|webm|mkv|avi|mxf)$/i;

export function classifyFootageUrl(raw: string): FootageLinkKind | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const path = url.pathname;
  if (host === 'youtu.be' || ((host === 'youtube.com' || host === 'm.youtube.com') && (url.searchParams.has('v') || /^\/(shorts|live)\//.test(path)))) {
    return 'youtube';
  }
  if (host === 'drive.google.com' && (/^\/file\/d\//.test(path) || url.searchParams.has('id'))) return 'drive';
  if ((host === 'dropbox.com' || host === 'dl.dropboxusercontent.com') && /^\/(s|scl\/fi|sh)\//.test(path)) return 'dropbox';
  if (/(^|\.)s3[.-]([a-z0-9-]+\.)?amazonaws\.com$/.test(host) && url.searchParams.has('X-Amz-Signature')) return 's3';
  if (VIDEO_EXT.test(path)) return 'video';
  return null;
}

export function extractFootageLinks(text: string): FootageLink[] {
  const seen = new Set<string>();
  const links: FootageLink[] = [];
  for (const match of text.match(URL_RE) ?? []) {
    const url = match.replace(/[.,;:!?]+$/, '');
    const kind = classifyFootageUrl(url);
    if (kind && !seen.has(url)) {
      seen.add(url);
      links.push({ url, kind });
    }
  }
  return links;
}

const lastSegment = (path: string) => {
  const seg = path.split('/').filter(Boolean).pop() ?? '';
  try {
    return decodeURIComponent(seg);
  } catch {
    return seg;
  }
};

export function footageLinkLabel(raw: string, kind: FootageLinkKind): string {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return raw;
  }
  const path = url.pathname;
  switch (kind) {
    case 'youtube': {
      const id = url.searchParams.get('v') ?? lastSegment(path);
      return id ? `YouTube · ${id}` : 'YouTube video';
    }
    case 'drive': {
      const id = url.searchParams.get('id') ?? path.match(/^\/file\/d\/([^/]+)/)?.[1];
      return id ? `Drive · ${id.length > 10 ? `${id.slice(0, 10)}…` : id}` : 'Drive file';
    }
    default:
      return lastSegment(path) || url.hostname;
  }
}
